import { useCallback } from 'react';
import Smartlook from 'smartlook-client';
import { getIsStageOrProdEnv } from 'utils/getEnv.ts';
import ValidationError from 'utils/ValidationError';

const useSmartlook = () => {
  const error = useCallback((whatFailed: string, e: unknown) => {
    if (!(window as any)?.smartlook || !getIsStageOrProdEnv()) {
      return;
    }
    let strError = whatFailed;
    if (e instanceof ValidationError) {
      strError = `${strError}: ValidationError: ${JSON.stringify(
        e.fieldErrors,
      )}`;
    } else if (e instanceof Error) {
      strError = `${strError}: Error: ${e.message}`;
    } else if (typeof e === 'string') {
      strError = `${strError}: ${e}`;
    }
    Smartlook.error(strError);
  }, []);

  /**
   * Tracks custom events in Smartlook that can't be tracked by click, URL, or input change.
   * Events and their properties will be visible in the Smartlook dashboard under the Events section.
   *
   * @param eventName - The name of the event to track (e.g., 'button-click', 'form-submit', 'open-category')
   * @param properties - Optional object containing event properties. Values can be string, number, or boolean.
   *
   * @example
   * // Track a simple button click
   * track('button-click');
   *
   * @example
   * // Track form submission with properties
   * track('form-submit', {
   *   formName: 'registration',
   *   success: true
   * });
   *
   * @example
   * // Track category opening
   * track('open-category', {
   *   name: 'category1'
   * });
   */
  const track = useCallback(
    (
      eventName: string,
      properties?: Record<string, string | number | boolean>,
    ) => {
      if (!(window as any)?.smartlook || !getIsStageOrProdEnv()) {
        return;
      }
      Smartlook.track(eventName, properties || {});
    },
    [],
  );

  const identify = (clientNumber?: string) => {
    if (clientNumber) {
      Smartlook.identify(clientNumber, {});
    }
  };
  return {
    identify,
    error,
    track,
  };
};

export default useSmartlook;
